import { create } from 'zustand';
import type { ViewportPreset } from '../types';
import { useMarkupStore } from '../store/useMarkupStore';

/**
 * Responsive preview.
 *
 * The page is loaded into a same-origin iframe sized to the device, the way
 * browser devtools do it. Inside that frame the viewport genuinely *is* 390px
 * wide, so media queries, `vw` units, `position: fixed`, `matchMedia` and the
 * host framework's own JS all behave exactly as on a phone — none of which is
 * true of restyling or scaling the live page, which is what the old
 * implementation did (and why it fell apart on Next.js, whose root layout owns
 * `<html>`/`<body>` and re-renders over any inline styles put on them).
 *
 * Everything that measures or listens to "the page" — pins, element picking,
 * screenshots, page metadata — asks `getMarkupWindow()` which window that
 * currently is, rather than reaching for the global `window`.
 */

/** `name` given to the preview iframe; how the page inside knows it's a preview. */
export const DEVICE_FRAME_NAME = 'markup-device-frame';

/** One representative size per device type, rather than a list of models. */
export const DEVICE_PRESETS: ViewportPreset[] = [
  { name: 'Mobile', width: 375, height: 812, deviceType: 'mobile' },
  { name: 'Tablet', width: 768, height: 1024, deviceType: 'tablet' },
];

/**
 * True inside the preview iframe.
 *
 * The host page loads the widget again in there (it's the same page), and that
 * copy must stay out of the way: a second widget, second set of pins, and a
 * second preview button nested inside the preview. `window.name` survives
 * navigation within the frame, so this holds on every page the reviewer clicks
 * through to — including full reloads, which a query-string flag would not.
 */
export function isDeviceFrame(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.name === DEVICE_FRAME_NAME && window.self !== window.top;
  } catch (_) {
    // Cross-origin parent: we're framed by someone else, not by the widget.
    return false;
  }
}

interface DeviceFrameState {
  /**
   * The preview's window, once a same-origin document has loaded in it. Null
   * while loading, when the site refused to be framed, or when the reviewer
   * followed a link off-site — in all of which there's nothing to mark up.
   */
  frame: Window | null;
  /** Current URL inside the preview; tracks client-side navigation too. */
  url: string | null;
  setFrame: (frame: Window | null, url: string | null) => void;
  setUrl: (url: string) => void;
}

/**
 * Deliberately separate from the persisted markup store: a `Window` isn't
 * serialisable, and none of this should outlive the preview.
 */
export const useDeviceFrameStore = create<DeviceFrameState>()((set) => ({
  frame: null,
  url: null,
  setFrame: (frame, url) => set({ frame, url }),
  setUrl: (url) => set({ url }),
}));

/**
 * The window being marked up: the device preview while one is live, otherwise
 * the page itself.
 */
export function getMarkupWindow(): Window {
  const { frame } = useDeviceFrameStore.getState();
  if (frame) {
    try {
      // Touching `document` throws if the frame has navigated cross-origin
      // since we last looked; fall back rather than blow up the caller.
      if (!frame.closed && frame.document) return frame;
    } catch (_) {
      /* fall through */
    }
  }
  return window;
}

/**
 * Hook form of `getMarkupWindow`, re-rendering when the preview changes.
 *
 * Null while a preview is open but has no page loaded (first load, mid
 * navigation, or blocked): there's nothing to mark up yet, and falling back to
 * the real page — hidden behind the preview — would let a click drop a pin on
 * a page the reviewer can't see.
 */
export function useMarkupWindow(): Window | null {
  const frame = useDeviceFrameStore((s) => s.frame);
  const previewing = useMarkupStore((s) => !!s.viewportMode);
  if (typeof window === 'undefined') return null;
  if (previewing) return frame;
  return window;
}
